import { useState } from 'react';
import { skillsAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2, Terminal, AlertTriangle, XCircle, CheckCircle2, Copy } from 'lucide-react';
import { toast } from 'sonner';

/**
 * La cuarta puerta de carga: pegar un `curl` que ya anda.
 *
 * **Por que existe.** Un constructor que arranca en blanco es un constructor que se abre una
 * vez. Quien tiene que cargar el ERP o el padron de Rentas ya tiene el comando andando en una
 * terminal; pegarlo es el camino mas corto entre "esto funciona" y "el agente puede usarlo".
 *
 * **Lo que la pantalla tiene que dejar clarisimo:** el `curl` trae la credencial adentro y
 * **no se guarda**. Si eso no se dice fuerte, alguien va a asumir que quedo configurada y la
 * herramienta va a fallar con 401 en el turno de un agente, que es donde menos se diagnostica.
 */
export default function ImportarCurl({ tenantId, capsuleId, onImportada }) {
  const [comando, setComando] = useState('');
  const [cargando, setCargando] = useState(false);
  const [resultado, setResultado] = useState(null);

  const importar = async () => {
    setCargando(true);
    setResultado(null);
    try {
      const { data } = await skillsAPI.importarCurl({
        comando, tenant_id: tenantId || '', capsule_id: capsuleId || '',
      });
      setResultado(data);
      if (data.ok && onImportada) onImportada(data.herramienta);
    } catch (err) {
      setResultado({ ok: false, mensaje: 'No se pudo importar', avisos: [], errores: [] });
    } finally {
      setCargando(false);
    }
  };

  const copiar = async () => {
    await navigator.clipboard.writeText(JSON.stringify(resultado.herramienta, null, 2));
    toast.success('Herramienta copiada');
  };

  return (
    <Card className="rounded-sm border-brand-border" data-testid="importar-curl">
      <CardHeader className="py-2 px-3 border-b border-brand-border">
        <CardTitle className="text-[10px] font-semibold uppercase tracking-[0.1em] text-zinc-600 flex items-center gap-1">
          <Terminal className="w-3 h-3" /> Pegar un curl que ya funcione
        </CardTitle>
      </CardHeader>
      <CardContent className="p-3 space-y-3">
        <p className="text-[10px] text-zinc-500">
          Pega el comando tal cual lo tenes andando. La credencial que traiga <b>no se guarda</b>:
          va en una conexion.
        </p>
        <textarea
          data-testid="curl-input"
          value={comando}
          onChange={(e) => setComando(e.target.value)}
          className="w-full h-28 bg-[#0A0A0A] text-zinc-200 p-3 font-mono text-[11px] leading-relaxed focus:outline-none resize-none rounded-sm"
          placeholder={'curl -X POST https://api.ejemplo.com/issues \\\n  -H "Authorization: Bearer ..." \\\n  -d \'{"title":"..."}\''}
        />
        <Button
          size="sm" data-testid="importar-curl-boton"
          className="bg-brand-primary hover:bg-[#001F6D] text-white rounded-sm text-xs h-7 px-3"
          onClick={importar} disabled={cargando || !comando.trim()}
        >
          {cargando ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
          Importar
        </Button>

        {resultado && !resultado.ok && (
          <div data-testid="curl-error" className="text-[11px] rounded-sm border border-red-200 bg-red-50 text-red-800 p-2 flex gap-2">
            <XCircle className="w-4 h-4 shrink-0" />
            <div>{resultado.mensaje}</div>
          </div>
        )}

        {resultado?.ok && (
          <div className="space-y-2">
            <div className="text-[11px] rounded-sm border border-green-200 bg-green-50 text-green-800 p-2 flex gap-2">
              <CheckCircle2 className="w-4 h-4 shrink-0" />
              <div>
                <b>{resultado.herramienta.nombre}</b> · {resultado.herramienta.metodo}{' '}
                <span className="font-mono opacity-70">{resultado.herramienta.url}</span>
                <div className="opacity-70">
                  {(resultado.herramienta.parametros || []).length} parametro(s) detectado(s)
                </div>
              </div>
            </div>

            {/* Los avisos son lo que el importador **no** pudo decidir. Esconderlos daria una
                herramienta que parece lista y no lo esta, que cuesta mas que un campo vacio. */}
            {(resultado.avisos || []).map((a, i) => (
              <div key={i} data-testid="curl-aviso"
                   className="text-[11px] rounded-sm border border-amber-200 bg-amber-50/60 text-amber-900 p-2 flex gap-2">
                <AlertTriangle className="w-4 h-4 shrink-0" />
                <div>{a.replace(/\*\*/g, '')}</div>
              </div>
            ))}

            {!onImportada && (resultado.errores || []).length > 0 && (
              <div data-testid="curl-faltantes" className="text-[11px] rounded-sm border border-brand-border p-2">
                <div className="font-semibold text-zinc-700 mb-1">Falta antes de poder guardarla</div>
                <ul className="list-disc pl-4 space-y-0.5 text-zinc-600">
                  {resultado.errores.map((e, i) => (
                    <li key={i}><span className="font-mono text-[10px] opacity-60">{e.codigo}</span> — {e.mensaje}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Con `onImportada`, la herramienta ya quedo agregada arriba: mostrar ademas el
                JSON y un boton de copiar sugiere que falta hacer algo con eso, y lo unico que
                falta es completar los campos que la lista de arriba ya esta pidiendo. Sin
                `onImportada` el componente esta solo y el JSON es la unica salida que tiene. */}
            {onImportada ? (
              <div className="text-[11px] text-zinc-600 border border-brand-border rounded-sm p-2">
                Se agrego a la lista de herramientas de arriba, ya abierta para completar.
              </div>
            ) : (
              <>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" data-testid="copiar-herramienta"
                          className="h-7 text-[10px] rounded-sm gap-1" onClick={copiar}>
                    <Copy className="w-3 h-3" /> Copiar la herramienta
                  </Button>
                </div>
                <pre className="bg-[#0A0A0A] text-zinc-300 p-3 rounded-sm text-[10px] overflow-x-auto max-h-56">
{JSON.stringify(resultado.herramienta, null, 2)}
                </pre>
              </>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
